import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { config } from '@/config';

export default function PromoModal() {
  const [isOpen, setIsOpen] = useState(false);
  const { enabled, storageKey, delay, campaign } = config.promoModal;

  useEffect(() => {
    if (!enabled) return;
    
    const hasSeenPromo = localStorage.getItem(storageKey);
    if (!hasSeenPromo) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [enabled, storageKey, delay]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem(storageKey, 'true');
  };

  if (!enabled) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md overflow-hidden rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, #020c1b 0%, #0a1628 50%, #051525 100%)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 100px rgba(34, 211, 238, 0.1)',
            }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full opacity-20"
                style={{ background: 'radial-gradient(circle, #22d3ee 0%, transparent 70%)' }}
              />
              <div
                className="absolute bottom-0 right-0 w-48 h-48 rounded-full opacity-15"
                style={{ background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)' }}
              />
            </div>

            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-30 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="relative z-10 p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', damping: 15 }}
                className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #22d3ee 0%, #8b5cf6 100%)',
                  boxShadow: '0 10px 40px rgba(34, 211, 238, 0.3)',
                }}
              >
                <span className="text-4xl">🧠</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-white mb-2"
              >
                {campaign.title}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-cyan-400 font-medium mb-3"
              >
                {campaign.subtitle}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-gray-400 text-sm mb-8"
              >
                {campaign.description}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col gap-3"
              >
                <a
                  href={campaign.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClose}
                  className="px-8 py-4 rounded-xl font-bold text-white transition-all hover:-translate-y-1 text-center"
                  style={{
                    background: 'linear-gradient(90deg, #22d3ee, #8b5cf6)',
                    boxShadow: '0 10px 30px rgba(34, 211, 238, 0.3)',
                  }}
                >
                  {campaign.ctaText} →
                </a>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 text-gray-500 hover:text-gray-300 text-sm transition-colors"
                >
                  稍后再说
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
